import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
  LoginWrapper,
  FormContainer,
  Form,
  Title,
  Button,
  LeftContainer,
  LoginLogo,
  ResetPasswordButton,
} from "./Login.styles";
import AppInput from "@components/AppInput";
import LoginLogoSVG from "@assets/img/login-logo.svg";
import EmailIcon from "@assets/img/email-icon.svg";
import PasswordIcon from "@assets/img/lock-icon.svg";
import EyeIcon from "@assets/img/eye-icon-yellow.svg";
import EyeIconLine from "@assets/img/eye-line.svg";
import EmailCheck from "@assets/img/check-rounded.svg";
import { useLocation, useNavigate } from "react-router-dom";
import { theme } from "@src/theme";
import { useAppDispatch } from "@src/redux/hooks";
import { loginThunk } from "@src/redux/thunks/authThunks";
import { useAuthState } from "@src/hooks/useAuth";
import LoginLeftContainer from "./LoginLeftContainer";
import { clearError } from "@src/redux/slices/authSlice";
import BottomLeftVersionTag from "@src/components/BottomLeftVersionTag";
import Spinner from "@src/components/Spinner";

interface LoginFormData {
  email: string;
  password: string;
}

const Login: React.FC = () => {
  const {
    isAuthenticated,
    errorMessage,
    loading: loadingLogin,
  } = useAuthState();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = React.useState(false);
  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(
      Yup.object().shape({
        email: Yup.string()
          .email("Invalid email address")
          .required("Email is required"),
        password: Yup.string()
          .min(8, "Password must be at least 8 characters")
          .required("Password is required"),
      })
    ),
  });
  useEffect(() => {
    if (isAuthenticated && !loadingLogin && !errorMessage) {
      const from = (location.state as { from?: string } | null)?.from;
      const isInternalPath =
        typeof from === "string" &&
        from.startsWith("/") &&
        !from.startsWith("//");
      navigate(isInternalPath ? from : "/", { replace: true });
    }
  }, [isAuthenticated, loadingLogin, errorMessage]);
  const onSubmit = (data: LoginFormData): void => {
    dispatch(loginThunk({ username: data.email, password: data.password }));
  };

  const handleResetPassword = (): void => {
    navigate("/forgot-password");
  };
  const inputValueEmail = watch("email");
  const isEmailValid = React.useCallback((): boolean => {
    const email = inputValueEmail;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return !errors.email && emailRegex.test(email);
  }, [inputValueEmail, errors.email]);

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, []);
  return (
    <LoginWrapper>
      <FormContainer>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <LoginLogo src={LoginLogoSVG} />
          <Title>WELCOME BACK</Title>
          {errorMessage && (
            <p style={{ color: theme.colors.red }}>{errorMessage}</p>
          )}
          {isAuthenticated && (
            <p style={{ color: theme.colors.green }}>Logged in!</p>
          )}
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <AppInput
                label="EMAIL"
                control={control}
                errors={errors}
                placeholder="Enter your email"
                {...field}
                disabled={loadingLogin}
                icon={<img src={EmailIcon} alt="Email" />}
                iconRight={
                  <span
                    style={{
                      userSelect: "none",
                      opacity: isEmailValid() ? 1 : 0.5,
                    }}
                  >
                    <img src={EmailCheck} alt="Email Check" />
                  </span>
                }
              />
            )}
          />

          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <AppInput
                label="PASSWORD"
                control={control}
                errors={errors}
                placeholder="Enter your password"
                type={showPassword ? "text" : "password"}
                {...field}
                disabled={loadingLogin}
                icon={<img src={PasswordIcon} alt="Password" />}
                iconRight={
                  <span
                    style={{ userSelect: "none" }}
                    onClick={() =>
                      !loadingLogin && setShowPassword(!showPassword)
                    }
                  >
                    {!showPassword ? (
                      <img src={EyeIcon} alt="Show Password" />
                    ) : (
                      <img src={EyeIconLine} alt="Hide Password" />
                    )}
                  </span>
                }
              />
            )}
          />

          <ResetPasswordButton
            type="button"
            onClick={handleResetPassword}
            disabled={loadingLogin}
          >
            Reset my password
          </ResetPasswordButton>
          <Button type="submit" disabled={loadingLogin}>
            {loadingLogin ? <Spinner small /> : "SIGN IN"}
          </Button>
        </Form>
      </FormContainer>
      <LeftContainer>
        <LoginLeftContainer />
      </LeftContainer>
      <BottomLeftVersionTag />
    </LoginWrapper>
  );
};

export default Login;
